import React, { memo } from 'react';
import { AlertTriangle, CheckCircle2, Database, Loader2, XCircle } from 'lucide-react';

import type { DocHubIndexStatus } from '../services/price-intel-service';

interface DocHubIndexProgressModalProps {
  open: boolean;
  documentName: string;
  status: DocHubIndexStatus | null;
  cancelling: boolean;
  onCancel: () => void;
  onClose: () => void;
}

function phaseLabel(phase: string | null | undefined): string {
  switch (phase) {
    case 'queued':
      return '排队等待';
    case 'splitting':
      return '切分检索块';
    case 'embedding':
      return '生成向量嵌入';
    case 'writing':
    case 'finalizing':
      return '写入向量索引';
    case 'ready':
      return '完成';
    case 'failed':
      return '失败';
    case 'cancelled':
      return '已取消';
    default:
      return '处理中';
  }
}

function strategyDescription(strategy: string | undefined): string {
  switch (strategy) {
    case 'on_demand':
      return '为每个章节 span 生成 embedding，供超大文档语义检索 + Rerank 使用';
    case 'standard_hybrid':
      return '为文档 span 生成 embedding，供混合向量检索使用';
    case 'tabular_search':
      return '构建表格行级检索结构';
    case 'direct':
      return '短文档无需向量索引，保留全文直送结构';
    default:
      return '正在建立文档检索索引';
  }
}

export const DocHubIndexProgressModal = memo(function DocHubIndexProgressModal({
  open,
  documentName,
  status,
  cancelling,
  onCancel,
  onClose,
}: DocHubIndexProgressModalProps) {
  if (!open || !status) return null;

  const progressPercent = Math.round(Math.max(0, Math.min(1, status.index_progress)) * 100);
  const isActive = status.index_status === 'queued' || status.index_status === 'running';
  const isSuccess = status.index_status === 'ready';
  const isFailed = status.index_status === 'failed';
  const isCancelled = status.index_status === 'cancelled';
  const showEmbedCounter =
    status.index_embed_total > 0
    && (status.index_phase === 'embedding' || status.index_phase === 'finalizing' || isSuccess || isCancelled);
  const checkpointCount = status.checkpoint_embedded_count ?? status.index_embedded_count;
  const checkpointTotal = status.checkpoint_embed_total || status.index_embed_total;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="索引重建进度"
      className="fixed inset-0 z-[120] flex items-center justify-center bg-slate-950/55 p-4 backdrop-blur-sm"
    >
      <div className="w-full max-w-lg rounded-2xl border border-[var(--color-system-quaternary)] bg-[var(--color-background-primary)] p-5 shadow-2xl">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[rgba(0,113,227,0.1)] text-[var(--color-key)]">
            {isActive ? <Loader2 size={18} className="animate-spin" /> : isSuccess ? <CheckCircle2 size={18} className="text-emerald-600" /> : isCancelled ? <AlertTriangle size={18} className="text-amber-600" /> : <XCircle size={18} className="text-red-500" />}
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="text-sm font-semibold text-[var(--color-system-primary)]">
              {isActive ? '正在重建检索索引' : isSuccess ? '索引重建完成' : isCancelled ? '索引重建已取消' : '索引重建失败'}
            </h3>
            <p className="mt-1 truncate text-[11px] text-[var(--color-system-secondary)]">{documentName}</p>
            {isCancelled && checkpointCount > 0 && (
              <p className="mt-1 text-[10px] text-amber-700">
                已完成部分已保存（{checkpointCount}/{checkpointTotal || '?'}），下次重建将自动继续
              </p>
            )}
            {isFailed && checkpointCount > 0 && (
              <p className="mt-1 text-[10px] text-amber-700">
                已保存 {checkpointCount} 个嵌入，再次重建将自动续传
              </p>
            )}
          </div>
        </div>

        <div className="mt-4 space-y-3 rounded-xl border border-[var(--color-system-quaternary)] bg-[var(--color-background-secondary)] p-4">
          <div className="flex items-center justify-between text-[11px]">
            <span className="font-medium text-[var(--color-system-primary)]">
              当前阶段：{phaseLabel(status.index_phase)}
            </span>
            <span className="font-semibold text-[var(--color-key)]">{progressPercent}%</span>
          </div>
          <div className="h-2 overflow-hidden rounded-full bg-[var(--color-background-primary)]">
            <div
              className={`h-full rounded-full transition-all duration-500 ${isFailed ? 'bg-red-500' : isCancelled ? 'bg-amber-500' : isSuccess ? 'bg-emerald-500' : 'bg-[var(--color-key)]'}`}
              style={{ width: `${progressPercent}%` }}
            />
          </div>
          <p className="text-[11px] leading-relaxed text-[var(--color-system-secondary)]">
            {status.index_progress_message || strategyDescription(status.index_strategy)}
          </p>
          {showEmbedCounter && (
            <p className="text-[10px] text-[var(--color-system-tertiary)]">
              向量进度：{status.index_embedded_count} / {status.index_embed_total} 个 span
              {isCancelled && checkpointCount > 0 ? '（已保存，可续传）' : ''}
            </p>
          )}
          {isSuccess && (
            <div className="grid grid-cols-2 gap-2 text-[10px]">
              <div className="rounded-lg border border-[var(--color-system-quaternary)] bg-[var(--color-background-primary)] px-2.5 py-2">
                <div className="text-[var(--color-system-tertiary)]">检索块</div>
                <div className="mt-0.5 font-semibold text-[var(--color-system-primary)]">{status.source_span_count}</div>
              </div>
              <div className="rounded-lg border border-[var(--color-system-quaternary)] bg-[var(--color-background-primary)] px-2.5 py-2">
                <div className="text-[var(--color-system-tertiary)]">向量切片</div>
                <div className="mt-0.5 font-semibold text-[var(--color-system-primary)]">{status.vector_chunk_count}</div>
              </div>
            </div>
          )}
          {(isFailed || isCancelled) && status.index_error && (
            <p className="rounded-lg border border-red-200 bg-red-50 px-2.5 py-2 text-[10px] text-red-600">
              {status.index_error}
            </p>
          )}
        </div>

        <div className="mt-4 flex items-center justify-end gap-2">
          {isActive && (
            <button
              type="button"
              onClick={onCancel}
              disabled={cancelling}
              className="rounded-lg border border-[var(--color-system-quaternary)] px-3 py-1.5 text-[11px] font-medium text-[var(--color-system-secondary)] transition hover:border-red-300 hover:text-red-600 disabled:opacity-50"
            >
              {cancelling
                ? '取消中…'
                : status.index_cancel_requested
                  ? '强制终止'
                  : '终止重建'}
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            disabled={isActive && !isCancelled && !cancelling && !status.index_cancel_requested ? false : false}
            className="inline-flex items-center gap-1.5 rounded-lg bg-[var(--color-key)] px-3 py-1.5 text-[11px] font-medium text-white disabled:opacity-50"
          >
            <Database size={12} />
            {isActive ? '后台继续' : '关闭'}
          </button>
        </div>
      </div>
    </div>
  );
});
